import { useMutation, useQueryClient } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export interface ReferencesDict {
  [key: string]: { id: string; type: 'fact' | 'chunk' };
}

interface UpdateManualThreadParams {
  caseId: string;
  threadId: string;
  question: string;
  answer: string;
  references: ReferencesDict;
}

interface UpdateManualThreadResponse {
  threadId: string;
  userMessageId: string;
  assistantMessageId: string;
}

// Transform answer from [1] format to [1]{uuid} format
const transformAnswerWithUUIDs = (answer: string, references: ReferencesDict): string => {
  return answer.replace(/\[(\d+)\]/g, (match, num) => {
    const ref = references[num];
    if (ref?.id) {
      return `[${num}]{${ref.id}}`;
    }
    return match;
  });
};

export const useUpdateManualThread = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<UpdateManualThreadResponse, Error, UpdateManualThreadParams>(
    async (params) => {
      // Transform answer to include UUIDs
      const transformedAnswer = transformAnswerWithUUIDs(params.answer, params.references);

      const fetchConfig = getFetchConfig({
        method: FetchMethod.PATCH,
        data: {
          question: params.question,
          answer: transformedAnswer,
          references: params.references,
        },
      });

      const response = await fetch(
        `${APIBaseChronos}/api/case/${params.caseId}/thread/${params.threadId}/manual`,
        fetchConfig,
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update manual thread');
      }

      return response.json();
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate threads query to refresh list
        queryClient.invalidateQueries([QueryEntity.Threads, variables.caseId]);
        // Invalidate thread status to refresh the view
        queryClient.invalidateQueries([QueryEntity.Thread, variables.caseId, variables.threadId]);
        // Invalidate messages to refresh the displayed answer
        queryClient.invalidateQueries([QueryEntity.ThreadMessages, variables.caseId, variables.threadId]);
      },
    },
  );
};
